/**
 * CL-388B–F — Portal forgot-password flow (callable + Resend via requestPasswordReset).
 */

import { callRequestPasswordReset as defaultCallRequestPasswordReset } from "../services/requestPasswordResetCallable";

export const PORTAL_PASSWORD_RESET_HEADING = "Reset your password";

export const PORTAL_PASSWORD_RESET_HELPER =
  "Enter the email address you use for your portal account. If an account exists, we'll send password reset instructions.";

export const PORTAL_PASSWORD_RESET_SUCCESS =
  "If an account exists for that email, password reset instructions have been sent.";

export const PORTAL_PASSWORD_RESET_SUBMIT_LABEL = "Send reset instructions";

export const PORTAL_PASSWORD_RESET_BACK_TO_SIGN_IN = "Back to sign in";

export const PORTAL_PASSWORD_RESET_FORGOT_LINK = "Forgot password?";

/** Shown on sign-in when user returns after an expired Firebase reset link (CL-388J). */
export const PORTAL_PASSWORD_RESET_EXPIRED_HINT =
  "If a password reset link expired, use Forgot password? below to request a new one.";

export const PORTAL_PASSWORD_RESET_PATH = "/client-portal";

export const PORTAL_PASSWORD_RESET_LOG_PREFIX = "[portal-password-reset]";

/** Origins allowed for actionCodeSettings continue URL (must match Firebase Authorized domains). */
export const PORTAL_PASSWORD_RESET_AUTHORIZED_ORIGINS = Object.freeze([
  "https://markbeattie.ca",
  "https://www.markbeattie.ca",
  "http://localhost:3000",
  "http://127.0.0.1:3000",
]);

function safeStr(value) {
  return String(value ?? "").trim();
}

export function isValidPortalPasswordResetEmail(email) {
  const em = safeStr(email).toLowerCase();
  if (!em || em.length > 254 || !em.includes("@")) return false;
  const domain = em.split("@").slice(1).join("@");
  return Boolean(domain && domain.includes("."));
}

function normalizeOrigin(origin) {
  return safeStr(origin).replace(/\/$/, "").toLowerCase();
}

/**
 * True when origin may be used as Firebase password-reset continue URL.
 * Hosting defaults (*.firebaseapp.com / *.web.app) are allowed when present in Authorized domains.
 */
export function isAuthorizedPasswordResetContinueOrigin(origin = "") {
  const base = normalizeOrigin(origin);
  if (!base) return false;
  if (PORTAL_PASSWORD_RESET_AUTHORIZED_ORIGINS.map((o) => o.toLowerCase()).includes(base)) {
    return true;
  }
  try {
    const host = new URL(base).hostname.toLowerCase();
    if (host === "localhost" || host === "127.0.0.1") return true;
    if (host.endsWith(".firebaseapp.com") || host.endsWith(".web.app")) return true;
  } catch {
    return false;
  }
  return false;
}

/**
 * Firebase actionCodeSettings — return to portal sign-in after reset email link flow.
 * @param {string} [origin]
 */
export function buildPortalPasswordResetActionCodeSettings(origin = "") {
  const base = safeStr(origin).replace(/\/$/, "");
  if (!base) return undefined;
  return {
    url: `${base}${PORTAL_PASSWORD_RESET_PATH}`,
    handleCodeInApp: false,
  };
}

/**
 * CL-388B-FIX1: only attach continue URL when origin is authorized; otherwise Firebase default.
 * Avoids auth/unauthorized-continue-uri and misconfigured local URLs blocking delivery.
 * @param {string} [origin]
 */
export function resolvePortalPasswordResetActionCodeSettings(origin = "") {
  if (!isAuthorizedPasswordResetContinueOrigin(origin)) return undefined;
  return buildPortalPasswordResetActionCodeSettings(origin);
}

/** Mask email for console-safe dev logs (never log passwords). */
export function maskPortalPasswordResetEmail(email) {
  const em = safeStr(email).toLowerCase();
  const at = em.indexOf("@");
  if (at < 1) return "(invalid)";
  const local = em.slice(0, at);
  const domain = em.slice(at);
  const visible = local.length <= 2 ? "*" : `${local.slice(0, 2)}***`;
  return `${visible}${domain}`;
}

export function isPortalPasswordResetDevLoggingEnabled() {
  if (process.env.NODE_ENV !== "production") return true;
  try {
    return typeof window !== "undefined" && window.localStorage?.getItem("mb_portal_reset_debug") === "1";
  } catch {
    return false;
  }
}

/**
 * Console-safe dev logging for password reset diagnostics (CL-388B-FIX1 / CL-388F).
 * @param {"callable-submit"|"callable-success"|"callable-error"|"submit"|"success"|"error"|"skip-settings"} phase
 * @param {object} [details]
 */
export function logPortalPasswordResetEvent(phase, details = {}) {
  if (!isPortalPasswordResetDevLoggingEnabled()) return;
  const payload = {
    phase,
    email: details.email ? maskPortalPasswordResetEmail(details.email) : undefined,
    code: safeStr(details.code) || undefined,
    message: safeStr(details.message) || undefined,
    usedActionCodeSettings: details.usedActionCodeSettings,
    continueUrl: details.continueUrl || undefined,
    origin: details.origin ? normalizeOrigin(details.origin) : undefined,
  };
  if (phase === "error" || phase === "callable-error") {
    console.warn(PORTAL_PASSWORD_RESET_LOG_PREFIX, payload);
  } else {
    console.info(PORTAL_PASSWORD_RESET_LOG_PREFIX, payload);
  }
}

/** Friendly errors that do not reveal whether an account exists. */
export function prettyPortalPasswordResetError(err) {
  const code = safeStr(err?.code).toLowerCase();
  if (code.includes("invalid-email") || code.includes("invalid-argument")) {
    return "Enter a valid email address and try again.";
  }
  if (code.includes("missing-email")) {
    return "Enter your email address to continue.";
  }
  if (code.includes("too-many-requests") || code.includes("resource-exhausted")) {
    return "Too many attempts. Please wait a few minutes and try again.";
  }
  if (
    code.includes("network-request-failed") ||
    code.includes("unavailable") ||
    code.includes("deadline-exceeded")
  ) {
    return "Network error. Check your connection and try again.";
  }
  if (
    code.includes("auth/unauthorized-continue-uri") ||
    code.includes("auth/invalid-continue-uri")
  ) {
    return "We could not send reset instructions right now. Please try again later.";
  }
  if (code.includes("operation-not-allowed")) {
    return "Password reset is temporarily unavailable. Please contact your project administrator.";
  }
  return "We could not send reset instructions right now. Please try again later.";
}

/**
 * Invoke requestPasswordReset callable with dev-safe logging (CL-388F).
 * @param {object} options
 * @param {string} options.email
 * @param {Function} [options.callRequestPasswordResetFn]
 */
export async function requestPortalPasswordReset({
  email,
  callRequestPasswordResetFn = defaultCallRequestPasswordReset,
}) {
  if (typeof callRequestPasswordResetFn !== "function") {
    throw new Error("callRequestPasswordResetFn is required");
  }
  const trimmed = safeStr(email);
  if (!isValidPortalPasswordResetEmail(trimmed)) {
    const err = new Error("Invalid email");
    err.code = "functions/invalid-argument";
    throw err;
  }

  logPortalPasswordResetEvent("callable-submit", { email: trimmed });

  try {
    const data = await callRequestPasswordResetFn({ email: trimmed });
    logPortalPasswordResetEvent("callable-success", { email: trimmed });
    return data;
  } catch (err) {
    logPortalPasswordResetEvent("callable-error", {
      email: trimmed,
      code: err?.code,
      message: err?.message,
    });
    throw err;
  }
}
